// Bot statistics service - fetches from the dashboard API's server-side
// /api/bot-stats/stats route, which holds the bot token privately (Mongo).
// Never call Discord directly from the browser with the bot token — a
// VITE_-prefixed env var gets baked into the client bundle and would leak it
// to every visitor.

const API_BASE = import.meta.env.VITE_API_URL || '';

interface BotStats {
  servers: number | string;
  users: number | string;
  commands: number | string;
  status: 'online' | 'offline' | 'idle' | 'unknown';
  lastUpdated: Date;
}

// Cache for stats to avoid rate limiting
let statsCache: BotStats | null = null;
let lastFetchTime: number = 0;
const CACHE_MS = 5 * 60 * 1000; // 5 minutes cache

/**
 * Format large numbers (1000 -> "1K+")
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M+';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K+';
  }
  return num.toString();
}

/**
 * Get command count from the bot's commands.json
 */
async function getCommandCount(): Promise<number> {
  try {
    const response = await fetch('/commands.json');
    if (response.ok) {
      const commands = await response.json();
      return Object.values(commands).reduce((total: number, category: any) => {
        if (category && typeof category === 'object') {
          return total + Object.keys(category).length;
        }
        return total;
      }, 0);
    }
    return 50;
  } catch {
    return 50;
  }
}

interface BotStatsResponse {
  totalServers: number;
  totalMembers: number;
  botInfo: { id: string; username: string } | null;
}

/**
 * Fetch bot stats from the dashboard API's server-side proxy
 */
async function fetchServerSideStats(): Promise<BotStatsResponse | null> {
  // API_BASE is intentionally '' in local dev — Vite proxies the relative
  // path to the local Express server (see vite.config.ts).
  try {
    const response = await fetch(`${API_BASE}/api/bot-stats/stats`);
    if (!response.ok) {
      console.error(`Bot stats API error: ${response.status}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch bot stats:', error);
    return null;
  }
}

/**
 * Main function to fetch bot statistics, with caching
 */
export async function fetchBotStats(): Promise<BotStats> {
  // Return cache if still fresh
  const now = Date.now();
  if (statsCache && (now - lastFetchTime) < CACHE_MS) {
    return statsCache;
  }

  const commandCount = await getCommandCount();
  const serverStats = await fetchServerSideStats();

  const stats: BotStats = serverStats
    ? {
        servers: formatNumber(serverStats.totalServers),
        users: formatNumber(serverStats.totalMembers),
        commands: commandCount,
        status: 'online',
        lastUpdated: new Date(),
      }
    : {
        // Fallback when the API is unreachable
        servers: '150+',
        users: '75K+',
        commands: commandCount,
        status: 'unknown',
        lastUpdated: new Date(),
      };

  statsCache = stats;
  lastFetchTime = now;

  return stats;
}

/**
 * Get cached status (doesn't make new requests)
 */
export function getCachedStatus(): 'online' | 'offline' | 'idle' | 'unknown' {
  return statsCache?.status || 'unknown';
}